import { useRef, useState, useEffect, useCallback } from "react";
import { Play, Pause, Square } from "lucide-react";
import { cn } from "@/lib/utils";

// Continuous surah reader: displays every verse and plays their audio sequentially.
export const SurahReader = ({ surah, showTranslation = true }) => {
  const audioRef = useRef(null);
  const [current, setCurrent] = useState(-1);
  const [playing, setPlaying] = useState(false);
  const ayahs = surah?.ayahs || [];

  const stop = useCallback(() => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
    setPlaying(false);
    setCurrent(-1);
  }, []);

  useEffect(() => stop, [surah?.number, stop]);

  const playFrom = useCallback((idx) => {
    if (idx >= ayahs.length) { setPlaying(false); setCurrent(-1); return; }
    const url = ayahs[idx]?.audio_url;
    setCurrent(idx);
    if (!url) { playFrom(idx + 1); return; }
    const a = audioRef.current;
    a.src = url;
    a.play();
    setPlaying(true);
  }, [ayahs]);

  const toggle = () => {
    const a = audioRef.current;
    if (playing) { a.pause(); setPlaying(false); }
    else if (current >= 0 && a.src) { a.play(); setPlaying(true); }
    else playFrom(0);
  };

  return (
    <div>
      <audio ref={audioRef} onEnded={() => playFrom(current + 1)} />
      <div className="flex items-center gap-3 mb-4">
        <button onClick={toggle} data-testid="reader-play-all"
          className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl bg-primary text-white font-extrabold shadow-[0_4px_0_#5a8ce0] active:translate-y-1 active:shadow-none transition-all">
          {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
          {playing ? "Pause" : "Lire toute la sourate"}
        </button>
        {current >= 0 && (
          <button onClick={stop} data-testid="reader-stop"
            className="inline-flex items-center gap-2 px-4 py-3 rounded-2xl border-2 border-slate-200 text-slate-500 font-bold hover:bg-slate-50">
            <Square className="w-4 h-4 fill-current" /> Arrêter
          </button>
        )}
        {current >= 0 && <span className="text-sm font-bold text-slate-400">Verset {ayahs[current]?.number_in_surah} / {ayahs.length}</span>}
      </div>

      <div className="space-y-3 max-h-[55vh] overflow-y-auto pr-1" data-testid="surah-reader">
        {ayahs.map((a, i) => (
          <div key={a.number_in_surah}
            className={cn("p-4 rounded-2xl border transition-colors",
              current === i ? "border-primary bg-primary/5" : "border-slate-100 bg-white")}>
            <div className="flex items-start gap-3">
              <span className="mt-1 w-7 h-7 shrink-0 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-extrabold">
                {a.number_in_surah}
              </span>
              <div className="flex-1">
                <p className="font-arabic text-2xl sm:text-3xl leading-[2.2] text-slate-900 text-right">{a.text_ar}</p>
                {showTranslation && <p className="text-sm text-slate-500 mt-2 border-t border-slate-100 pt-2">{a.translation_fr}</p>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
